// @/services/preRegistrationService.ts
import api from '@/services/api'

interface Addon {
  id: string
  product_id: string
  name: string
  description: string
  original_price: number
  price: number
  thumbnail_url?: string
}

interface PreRegistrationAttributes {
  email: string
  name: string
  phone: string
  document: string
  productId: string
  dynamicProduct: string
  dynamicLine: string
  curatoryId?: string
  paymentMethod: string
  installment?: string
  zipCode: string
  street: string
  number: string
  address_line_2?: string
  neighborhood: string
  city: string
  state: string
  selectedAddons?: Addon[]
  couponId?: string
}

interface OrderItem {
  product_id: string
  dynamic_product_id: string
  dynamic_line_id: string
  curatory_id: string | null
  quantity: number
  sub_order_items_attributes?: Array<{
    product_id: string
    quantity: number
  }>
}

interface PreRegistrationBody {
  email: string
  name: string
  phone: string
  document: string
  company_id: string
  dynamic_product_id: string
  product_quantity: number
  dynamic_line_id: string
  product_id: string
  payment_method: string
  payment_condition: string
  coupon_id?: string
  curatory_id?: string
  address_attributes: {
    zip_code: string
    street: string
    number: string
    address_line_2: string
    neighborhood: string
    city: string
    state: string
  }
  order_items_attributes: OrderItem[]
}

const createBody = (
  attributes: PreRegistrationAttributes,
): PreRegistrationBody => {
  const companyId = import.meta.env.VITE_COMPANY_ID ?? ''

  const orderItem: OrderItem = {
    product_id: attributes.productId,
    dynamic_product_id: attributes.dynamicProduct,
    dynamic_line_id: attributes.dynamicLine,
    curatory_id: attributes.curatoryId || null,
    quantity: 1,
  }

  // Adicionar addons se existirem
  if (attributes.selectedAddons && attributes.selectedAddons.length > 0) {
    orderItem.sub_order_items_attributes = attributes.selectedAddons.map(
      addon => ({
        product_id: addon.product_id || addon.id,
        quantity: 1,
      }),
    )
  }

  const body: PreRegistrationBody = {
    email: attributes.email,
    name: attributes.name,
    phone: attributes.phone,
    document: attributes.document,
    company_id: companyId,
    dynamic_product_id: attributes.dynamicProduct,
    product_quantity: 1,
    dynamic_line_id: attributes.dynamicLine,
    product_id: attributes.productId,
    payment_method: attributes.paymentMethod,
    payment_condition: attributes.installment || '1',
    coupon_id: attributes.couponId || '',
    curatory_id: attributes.curatoryId || '',
    address_attributes: {
      zip_code: attributes.zipCode,
      street: attributes.street,
      number: attributes.number,
      address_line_2: attributes.address_line_2 || '',
      neighborhood: attributes.neighborhood,
      city: attributes.city,
      state: attributes.state,
    },
    order_items_attributes: [orderItem],
  }

  return body
}

export const usePreRegistration = async (
  attributes: PreRegistrationAttributes,
) => {
  const companyId = import.meta.env.VITE_COMPANY_ID ?? ''

  const response = await api.post(
    '/pre_registrations',
    {
      pre_registration: createBody(attributes),
    },
    {
      headers: { 'X-Current-Company-ID': companyId },
    },
  )

  return response
}

export default usePreRegistration
